/**
 * Sawyer 08 meds-feeding (A3) — one muted line under a Day list dog: when it eats
 * and when its medications are due. Sawyer (memo3): "add feedings and
 * medications to the day list at the time".
 *
 * The facts come from `/rollup`'s per-dog `care`, computed on the server by
 * the same core rules as the Meds & feeding report (care-schedule.ts), so
 * the row and the report cannot disagree (DESIGN-PHILOSOPHY #1). Renders
 * nothing for grooming rows, service legs, a caller with no pets read, or an
 * older server that sends no `care` (deploy-window rule). Principle 5: a
 * boarder with no feeding notes says so; text-only medication says "see
 * notes"; a medication whose time is not set says so.
 */
import type { DayRollupCare } from './dashboard-api.js';
import { slotShort } from '../../lib/care-labels.js';
import './day-care-line.css';

const MEAL_SLOT_WORD: Record<'breakfast' | 'lunch' | 'dinner', string> = {
  breakfast: 'AM',
  lunch: 'Noon',
  dinner: 'PM',
};

/** The line's text, or null when there is nothing to say. Exported for tests. */
export function dayCareLineText(care: DayRollupCare | null | undefined, service: string): string | null {
  if (!care) return null;
  const parts: string[] = [];
  const isBoarding = service === 'boarding';
  if (isBoarding) {
    if (!care.hasFeedingNotes) parts.push('No feeding instructions on file');
    else if (care.meals.length > 0) parts.push(`Feeds ${care.meals.map((m) => MEAL_SLOT_WORD[m]).join(' · ')}`);
  } else if (care.eatsLunch) {
    parts.push('Lunch');
  }

  const meds = care.meds
    .map((m) => {
      // The server's placement (core placeMedicationFor, the same answer the
      // report gives); derived from slotSource + slots for an older server.
      const placement =
        m.placement ??
        (m.slotSource === 'frequency_default' || m.slotSource === 'unscheduled'
          ? 'times_not_set'
          : m.slots.length === 0
            ? 'at_home'
            : 'scheduled');
      if (placement === 'times_not_set') return `${m.name} (times not set)`;
      if (placement === 'at_home') return null; // given at home (a daycare dog's AM dose)
      return `${m.name} ${m.slots.map(slotShort).join(', ')}`;
    })
    .filter((m): m is string => m !== null);
  if (meds.length > 0) parts.push(`Meds: ${meds.join('; ')}`);
  else if (care.meds.length === 0 && care.hasMedicationNotes) parts.push('Meds: see notes');

  return parts.length > 0 ? parts.join(' · ') : null;
}

export function DayCareLine({
  care,
  service,
  testId,
}: {
  care: DayRollupCare | null | undefined;
  /** The row's service engine key (`boarding`, `daycare`, …). */
  service: string;
  testId?: string;
}): JSX.Element | null {
  const text = dayCareLineText(care, service);
  if (!text) return null;
  return (
    <span className="ferro-daylist__care" data-testid={testId}>
      {text}
    </span>
  );
}
